import { ChangeDetectionStrategy, Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatListOption } from '@angular/material/list';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { CourseCompositeId, SyllabusQuery } from '@examdojo/category';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { SelectionListComponent, SelectionListOption } from '@examdojo/ui/selection-list';
import { isNotNullish } from '@examdojo/util/nullish';
import { IonButton } from '@ionic/angular/standalone';
import { TranslocoPipe } from '@jsverse/transloco';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';
import { RootUrlParts } from '../../../../../app.model';
import { SettingsUrlParts } from '../../../../../settings/settings.model';
import { CourseManagementService } from '../../../../course-management/course-management.service';

@Component({
  selector: 'dojo-course-select-menu',
  imports: [
    IonButton,
    MatMenu,
    MatMenuContent,
    MatMenuTrigger,
    IconComponent,
    TranslocoPipe,
    ButtonComponent,
    SelectionListComponent,
  ],
  templateUrl: './course-select-menu.component.html',
  styleUrls: ['./course-select-menu.component.scss', '../../sidebar-top-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseSelectMenuComponent {
  constructor(
    private readonly syllabusQuery: SyllabusQuery,
    private readonly courseManagementService: CourseManagementService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
  ) {
    this.setFormOnCourseChange().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  readonly courseSelectionCtrl = new FormControl<CourseCompositeId[]>([]);

  readonly coursesOptions$ = this.courseManagementService.subscribedCourses$.pipe(
    map((courses) =>
      courses.map(
        (course): SelectionListOption<CourseCompositeId> => ({
          value: course.compositeId,
          label: course.longLabel,
          figure: course.icon ? { type: 'icon', icon: course.icon } : undefined,
        }),
      ),
    ),
  );

  readonly state = connectState({
    activeSyllabus: this.syllabusQuery.active$,
    activeCourse: this.courseManagementService.activeCourse$,
    courses: this.courseManagementService.subscribedCourses$,
    coursesOptions: this.coursesOptions$,
    hasSyllabiToAdd: this.courseManagementService.hasSyllabiToAdd$,
  });

  openNewCourseDialog(): void {
    this.courseManagementService.openNewCourseDialog().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  navigateToCoursesSettings(): void {
    this.router.navigate([RootUrlParts.Settings, SettingsUrlParts.Courses]);
  }

  onCourseSelected(selectedOptions: MatListOption[]): void {
    if (selectedOptions && selectedOptions.length > 0) {
      const selectedCourseId = selectedOptions[0].value;
      const course = this.state.courses.find((c) => c.compositeId === selectedCourseId);
      if (course) {
        this.courseManagementService
          .updateSelectedCourse({
            syllabusId: course.syllabus_id,
            courseLevel: course.course_level,
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      }
    }
  }

  private setFormOnCourseChange() {
    return this.courseManagementService.activeCourse$.pipe(
      map((course) => course?.compositeId),
      filter(isNotNullish),
      distinctUntilChanged(),
      tap((compositeId) => this.courseSelectionCtrl.setValue([compositeId])),
    );
  }
}
